// Sort by any field and group by (phase 3 §4.2, §4.3): which fields of a
// collection's types the select offers. The server checks the same keys.
import { fieldKey } from '../../fieldKeys';
import { usedFields } from '../../kinds';

/** Field types a list sorts on: a value that orders (a vocab or select by its first code). */
export const SORTABLE_TYPES = ['text', 'number', 'date', 'select', 'radio', 'vocab', 'rating', 'money', 'measure', 'yesno', 'identifier'];
/** Field types a list groups by (their first value): the coded checklists and yes/no. */
export const GROUPABLE_TYPES = ['select', 'radio', 'checkbox', 'vocab', 'yesno'];

export const TYPE_GROUP = '@type';
export const PLACE_GROUP = '@place';
export const YEAR_GROUP = '@year';

/** Every used field of the types, by key, the default type's first and in its order (the first definition wins). */
export const collectionFields = (inventory, itemTypes) => {
  const types = [...(itemTypes || [])].sort((a, b) => Number(!!b.is_default) - Number(!!a.is_default));
  const lists = types.length ? types.map((type) => type.fields) : [inventory && inventory.fields];
  const seen = new Set();
  const out = [];
  lists.forEach((fields) => usedFields(fields).forEach((field) => {
    const key = fieldKey(field);
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(field);
  }));
  return out;
};

/**
 * "Other field…": each sortable field, ascending and descending, but the
 * title and the year, which the common choices already offer.
 */
export const otherFieldSorts = (t, fields) => fields
  .filter((field) => SORTABLE_TYPES.includes(field.type || 'text') && !['title', 'year'].includes(field.role))
  .flatMap((field) => ['asc', 'desc'].map((direction) => ({
    id: `${fieldKey(field)}:${direction}`,
    label: t(direction === 'asc' ? 'collection.sort.fieldAsc' : 'collection.sort.fieldDesc', { name: field.name }),
    group: 'other',
  })));

/** Whether a field can be a group: a series, the creators, or a checklist of codes. */
export const groupable = (field) => field.role === 'series'
  || (field.role === 'creators' && field.type === 'tags')
  || GROUPABLE_TYPES.includes(field.type || 'text');

/**
 * The group select: none, the type (several types), the place (a location
 * type), the decade (a field plays the year, or is keyed `year`), then the series, the creators
 * and the coded fields.
 */
export const groupOptions = (t, { fields, typeCount, hasPlaces }) => [
  { id: '', label: t('collection.group.none') },
  ...(typeCount > 1 ? [{ id: TYPE_GROUP, label: t('collection.group.type') }] : []),
  ...(hasPlaces ? [{ id: PLACE_GROUP, label: t('collection.group.place') }] : []),
  ...(fields.some((field) => field.role === 'year' || fieldKey(field) === 'year') ? [{ id: YEAR_GROUP, label: t('collection.group.decade') }] : []),
  ...fields.filter(groupable).map((field) => ({ id: fieldKey(field), label: field.name })),
];
